import { format } from 'date-fns';
import { useMySuspendedMembers } from '@/hooks/members/useMySuspendedMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import type { SuspendedMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<SuspendedMemberRow>;

/**
 * Suspended Members — the practice's members whose membership is suspended, for following up on
 * billing holds. In the server's last-name order until a column is sorted.
 *
 * The report is the 25 columns below, in `csv.order`: legacy's report less its Title column, which
 * this view's API doesn't return. Days Past Due and Last AWP are the table's own fields; the table's
 * Patient and Phone combine two values each, so the report has its own name and phone columns.
 *
 * Priority tags isn't sortable here, as decided for this view: every row carries the Suspended tag.
 */
export const suspendedMembersDataset: MemberDataset<SuspendedMemberRow> = {
  key: 'suspended',
  rowKey: (row) => row.id,
  useRows: useMySuspendedMembers,
  alphaKey: (row) => row.lastName,
  // The same provisional ceiling as All Active; a practice's suspended members are a subset of it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_Suspended_Members_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round, with the MDVIP ID.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'daysPastDue',
      label: 'memberViews.columns.daysPastDue',
      width: 'md',
      value: (row) => row.billingDaysPastDue,
      format: 'number',
      table: true,
      sortable: true,
      csv: { order: 14, label: 'memberViews.csv.columns.daysPastDue' },
    },
    {
      id: 'phone',
      label: 'memberViews.columns.phone',
      width: 'lg',
      value: (row) => row.mobilePhone ?? row.phone,
      format: 'phone',
      table: true,
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      value: (row) => row.priorityTags,
      table: true,
    },
    {
      id: 'lastAwp',
      label: 'memberViews.columns.lastAwp',
      width: 'sm',
      value: (row) => row.lastAwpDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 9, label: 'memberViews.csv.columns.lastAwpDate' },
    },
    reportOnly('lastName', 1, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('firstName', 2, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('mdvipId', 3, 'memberViews.csv.columns.mdvipId', (row) => row.mdvipId),
    reportOnly('established', 4, 'memberViews.csv.columns.dateEstablished', (row) => row.memberSince, 'date'),
    reportOnly('renewalDate', 5, 'memberViews.csv.columns.renewalDate', (row) => row.renewalDate, 'date'),
    // The phone numbers as the API returns them, as legacy's report printed them.
    reportOnly('homePhone', 6, 'memberViews.csv.columns.phone', (row) => row.phone),
    reportOnly('cellPhone', 7, 'memberViews.csv.columns.cellPhone', (row) => row.mobilePhone),
    reportOnly('email', 8, 'memberViews.csv.columns.email', (row) => row.email),
    reportOnly('status', 10, 'memberViews.csv.columns.status', (row) => row.memberStatus),
    reportOnly('dob', 11, 'memberViews.csv.columns.dob', (row) => row.dateOfBirth, 'date'),
    reportOnly(
      'paymentSchedule',
      12,
      'memberViews.csv.columns.paymentSchedule',
      (row) => row.currentAgreementPaymentSchedule,
    ),
    // The current agreement's fee, not the membership's list price.
    reportOnly(
      'membershipFee',
      13,
      'memberViews.csv.columns.membershipFee',
      (row) => row.currentAgreementMsrp,
      'currency',
    ),
    // The balance owed in each past-due bucket. Headed per the legacy report's requirement, one
    // bucket later than the API's field names (`aging30` is "31-60 Days"), and in dollars, which
    // that requirement asked for and legacy never applied.
    reportOnly('aging0to30', 15, 'memberViews.csv.columns.aging0to30', (row) => row.agingCurrent, 'currency'),
    reportOnly('aging31to60', 16, 'memberViews.csv.columns.aging31to60', (row) => row.aging30, 'currency'),
    reportOnly('aging61to90', 17, 'memberViews.csv.columns.aging61to90', (row) => row.aging60, 'currency'),
    reportOnly('aging91to120', 18, 'memberViews.csv.columns.aging91to120', (row) => row.aging90, 'currency'),
    reportOnly('aging120Plus', 19, 'memberViews.csv.columns.aging120Plus', (row) => row.aging120, 'currency'),
    reportOnly('autoPay', 20, 'memberViews.csv.columns.autoPay', (row) => row.autoPay, 'yesBlank'),
    reportOnly('lastPaymentDate', 21, 'memberViews.csv.columns.lastPaymentDate', (row) => row.lastPaymentDate, 'date'),
    reportOnly(
      'lastPaymentAmount',
      22,
      'memberViews.csv.columns.lastPaymentAmount',
      (row) => row.lastPaymentAmount,
      'currency',
    ),
    reportOnly('amountDue', 23, 'memberViews.csv.columns.amountDue', (row) => row.currentBalanceFormula, 'currency'),
    // "No" survives here, unlike Auto Pay: legacy printed this field's own Yes/No.
    reportOnly(
      'billToCorporateAccount',
      24,
      'memberViews.csv.columns.billToCorporateAccount',
      (row) => row.billToNationalAccount,
      'yesNoBlank',
    ),
    reportOnly('corporateAccount', 25, 'memberViews.csv.columns.corporateAccount', (row) => row.nationalAccountName),
  ],
};
